import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, Download, ChevronUp, ChevronDown, TrendingUp } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { dashboardService, CompletedBacktestSummary } from '../../../services/DashboardService';
import { BacktestResultsChart } from '../../../components/library/backtest-analytics/BacktestResultsChart';
import { BacktestSeriesPayload } from '../../../components/utils/returns';
import { BacktestRequest } from '../../order-ticket/strategy-backtest/config/types';
import { downloadBacktestAsExcel } from '../../../components/utils/backtestExport';
import { StrategyConfigView } from '../../order-ticket/strategy-backtest/components/StrategyConfigView';
import compactStyles from '../../../components/trading/CompactTable.module.css';
import type { WidgetInstanceProps } from '../../../components/grid-dashboard';

/* ── Sort helpers ──────────────────────────────────────────────────── */

type SortField = 'backtest_name' | 'start_date' | 'end_date' | 'total_pnl';
type SortDir = 'asc' | 'desc';

function comparator(a: CompletedBacktestSummary, b: CompletedBacktestSummary, field: SortField, dir: SortDir): number {
  let cmp = 0;
  if (field === 'total_pnl') cmp = a.total_pnl - b.total_pnl;
  else cmp = (a[field] ?? '').localeCompare(b[field] ?? '');
  return dir === 'asc' ? cmp : -cmp;
}

/* ── Format helpers ────────────────────────────────────────────────── */

const fmtDate = (d: string): string => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const fmtPnl = (v: number): string => {
  const s = Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v >= 0 ? s : `-${s}`;
};

const fmtPct = (v: number): string =>
  `${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const toBacktestRequest = (config: Record<string, unknown> | null | undefined): BacktestRequest | null => {
  if (!config || typeof config !== 'object') return null;
  return config as unknown as BacktestRequest;
};

/* ── StatsPane ─────────────────────────────────────────────────────── */

const StatsPane: React.FC<{ stats: BacktestSeriesPayload | null; onDownload: () => void; hasData: boolean }> = ({ stats, onDownload, hasData }) => {
  if (!stats) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', color: 'var(--tr-text-muted, #888)', gap: '8px' }}>
      <BarChart3 size={28} style={{ opacity: 0.25 }} />
      <span>No statistics available</span>
    </div>
  );

  const rows: { label: string; value: string; color: string }[] = [
    { label: 'Net Capital Gain', value: fmtPnl(stats.net_capital_gain), color: stats.net_capital_gain >= 0 ? 'var(--tr-pnl-positive, #22c55e)' : 'var(--tr-pnl-negative, #ef4444)' },
    { label: 'Total Trades', value: String(stats.total_trades), color: 'var(--tr-text-secondary, #ccc)' },
    { label: 'Avg PnL / Trade', value: fmtPnl(stats.average_pnl_per_trade), color: stats.average_pnl_per_trade >= 0 ? 'var(--tr-pnl-positive, #22c55e)' : 'var(--tr-pnl-negative, #ef4444)' },
    { label: 'Hit Ratio', value: fmtPct(stats.hit_ratio), color: 'var(--tr-text-secondary, #ccc)' },
    { label: 'Max Gain', value: fmtPnl(stats.max_gain), color: 'var(--tr-pnl-positive, #22c55e)' },
    { label: 'Max Loss', value: fmtPnl(Math.abs(stats.max_loss)), color: 'var(--tr-pnl-negative, #ef4444)' },
    { label: 'Max Drawdown', value: fmtPnl(Math.abs(stats.max_drawdown)), color: 'var(--tr-pnl-negative, #ef4444)' },
    { label: 'Avg Cost / Trade', value: fmtPnl(Math.abs(stats.average_cost_per_trade)), color: 'var(--tr-text-secondary, #ccc)' },
    { label: 'Max Cost', value: fmtPnl(Math.abs(stats.max_cost)), color: 'var(--tr-text-secondary, #ccc)' },
    { label: 'Avg Profit %', value: fmtPct(stats.average_profit_percent), color: stats.average_profit_percent >= 0 ? 'var(--tr-pnl-positive, #22c55e)' : 'var(--tr-pnl-negative, #ef4444)' },
    { label: 'Max Profit %', value: fmtPct(stats.max_profit_percent), color: 'var(--tr-pnl-positive, #22c55e)' },
    { label: 'Max Loss %', value: fmtPct(Math.abs(stats.max_loss_percent)), color: 'var(--tr-pnl-negative, #ef4444)' },
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        {rows.map((r) => (
          <div key={r.label} style={{ background: 'var(--tr-bg-tertiary, #1a1a2e)', border: '1px solid var(--tr-row-separator, rgba(255,255,255,0.06))', borderRadius: '8px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '0.6875rem', fontWeight: 500, color: 'var(--tr-text-muted, #888)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{r.label}</span>
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: r.color }}>{r.value}</span>
          </div>
        ))}
      </div>
      {hasData && (
        <button type="button" onClick={onDownload} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', marginTop: '10px', background: 'var(--tr-accent, #3b82f6)', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
          <Download size={14} /> Download Excel
        </button>
      )}
    </>
  );
};

/* ── SortIcon ──────────────────────────────────────────────────────── */

const SortIcon: React.FC<{ field: SortField; activeField: SortField; activeDir: SortDir }> = ({ field, activeField, activeDir }) => {
  const isActive = activeField === field;
  const Icon = isActive && activeDir === 'desc' ? ChevronDown : ChevronUp;
  return (
    <Icon
      size={12}
      style={{
        marginLeft: '4px',
        opacity: isActive ? 1 : 0.5,
        color: isActive ? 'var(--tr-accent, #3b82f6)' : undefined,
        verticalAlign: 'middle',
      }}
    />
  );
};

/* ── Inner component (assumes AuthContext is available) ─────────────── */

const BacktestsInner: React.FC = () => {
  const { userUuid, getToken, isAuthenticated } = useAuth();

  const [backtests, setBacktests] = useState<CompletedBacktestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resultsData, setResultsData] = useState<BacktestSeriesPayload | null>(null);
  const [configData, setConfigData] = useState<BacktestRequest | null>(null);
  const [leftTab, setLeftTab] = useState<'stats' | 'config'>('stats');

  const [sortField, setSortField] = useState<SortField>('backtest_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  /* ── Load backtests on mount ─────────────────────────────────────── */

  useEffect(() => {
    if (!isAuthenticated || !userUuid) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const resp = await dashboardService.listBacktests(userUuid, token);
        if (!cancelled) setBacktests(resp.backtests);
      } catch (err) {
        if (!cancelled) {
          const msg = err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Failed to load backtests';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isAuthenticated, userUuid, getToken]);

  /* ── Load backtest detail ────────────────────────────────────────── */

  const loadDetail = useCallback(async (name: string) => {
    if (!userUuid) return;
    setDetailLoading(true);
    setResultsData(null);
    setConfigData(null);
    try {
      const token = await getToken();
      if (!token) return;
      const [rResp, cResp] = await Promise.all([
        dashboardService.getBacktestResultsByName(userUuid, token, name),
        dashboardService.getBacktestConfigByName(userUuid, token, name),
      ]);
      if (rResp.results) setResultsData(rResp.results as BacktestSeriesPayload);
      if (cResp.config_data) setConfigData(toBacktestRequest(cResp.config_data as unknown as Record<string, unknown>));
    } catch (e) {
      console.error('Failed to load backtest detail:', e);
    } finally {
      setDetailLoading(false);
    }
  }, [userUuid, getToken]);

  /* ── Row selection ───────────────────────────────────────────────── */

  const handleSelect = useCallback((name: string) => {
    if (name === selectedName) {
      setSelectedName(null);
      setResultsData(null);
      setConfigData(null);
    } else {
      setSelectedName(name);
      loadDetail(name);
    }
  }, [selectedName, loadDetail]);

  /* ── Sorting ─────────────────────────────────────────────────────── */

  const sorted = useMemo(
    () => [...backtests].sort((a, b) => comparator(a, b, sortField, sortDir)),
    [backtests, sortField, sortDir],
  );

  const toggleSort = useCallback((field: SortField) => {
    if (field === sortField) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  }, [sortField]);

  /* ── Download handler ────────────────────────────────────────────── */

  const handleDownload = useCallback(() => {
    if (resultsData?.raw_data) downloadBacktestAsExcel(resultsData.raw_data);
  }, [resultsData]);

  const pnlStats = useMemo(() => resultsData ?? null, [resultsData]);

  /* ── Render ──────────────────────────────────────────────────────── */

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', color: 'var(--tr-text-primary, #e2e8f0)' }}>
      {/* Detail View */}
      {selectedName && (
        <div style={{ display: 'flex', flex: 1, minHeight: 0, borderBottom: '1px solid var(--tr-row-separator, rgba(255,255,255,0.06))' }}>
          {/* Left panel: Stats / Config tabs */}
          <div style={{ width: '340px', minWidth: '280px', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--tr-row-separator, rgba(255,255,255,0.06))', overflowY: 'auto' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid var(--tr-row-separator, rgba(255,255,255,0.06))', background: 'var(--tr-bg-secondary, rgba(255,255,255,0.02))', position: 'sticky', top: 0, zIndex: 5 }}>
              <button
                type="button"
                onClick={() => setLeftTab('stats')}
                style={{
                  flex: 1, padding: '8px 0', textAlign: 'center', fontSize: '0.8125rem', fontWeight: 600,
                  color: leftTab === 'stats' ? 'var(--tr-accent, #3b82f6)' : 'var(--tr-text-muted, #888)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: leftTab === 'stats' ? '2px solid var(--tr-accent, #3b82f6)' : '2px solid transparent',
                }}
              >
                Statistics
              </button>
              <button
                type="button"
                onClick={() => setLeftTab('config')}
                style={{
                  flex: 1, padding: '8px 0', textAlign: 'center', fontSize: '0.8125rem', fontWeight: 600,
                  color: leftTab === 'config' ? 'var(--tr-accent, #3b82f6)' : 'var(--tr-text-muted, #888)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: leftTab === 'config' ? '2px solid var(--tr-accent, #3b82f6)' : '2px solid transparent',
                }}
              >
                Configuration
              </button>
            </div>
            <div style={{ flex: 1, padding: '12px', overflowY: 'auto' }}>
              {detailLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>Loading…</div>
              ) : leftTab === 'stats' ? (
                <StatsPane stats={pnlStats} onDownload={handleDownload} hasData={!!resultsData?.raw_data} />
              ) : (
                <StrategyConfigView config={configData} />
              )}
            </div>
          </div>

          {/* Right panel: Chart */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {detailLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', flex: 1 }}>Loading…</div>
            ) : resultsData ? (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <BacktestResultsChart data={resultsData} height="100%" minimal />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--tr-text-muted, #888)', gap: '8px' }}>
                <TrendingUp size={36} style={{ opacity: 0.25 }} />
                <span>No results available</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backtest Table */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: selectedName ? undefined : 1, minHeight: '120px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px',
          background: 'var(--tr-bg-secondary, rgba(255,255,255,0.02))',
          borderBottom: '1px solid var(--tr-row-separator, rgba(255,255,255,0.06))',
          fontSize: '0.8125rem', fontWeight: 700,
        }}>
          <span>
            Saved Backtests
            {!loading && (
              <span style={{ fontSize: '0.75rem', color: 'var(--tr-text-muted, #888)', marginLeft: '6px' }}>
                ({backtests.length})
              </span>
            )}
          </span>
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', flex: 1 }}>
            Loading…
          </div>
        ) : error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', color: 'var(--tr-pnl-negative, #ef4444)', fontSize: '0.875rem', flex: 1 }}>
            {error}
          </div>
        ) : backtests.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', color: 'var(--tr-text-muted, #888)', gap: '8px', flex: 1 }}>
            <BarChart3 size={36} style={{ opacity: 0.3 }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>No backtests yet</span>
            <span style={{ fontSize: '0.8125rem' }}>Run a backtest from the chat and save it to see it here.</span>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <table className={compactStyles.compactTable}>
              <thead>
                <tr>
                  <th className={compactStyles.sortableHeader} onClick={() => toggleSort('backtest_name')}>
                    Name <SortIcon field="backtest_name" activeField={sortField} activeDir={sortDir} />
                  </th>
                  <th className={compactStyles.sortableHeader} onClick={() => toggleSort('start_date')}>
                    Start Date <SortIcon field="start_date" activeField={sortField} activeDir={sortDir} />
                  </th>
                  <th className={compactStyles.sortableHeader} onClick={() => toggleSort('end_date')}>
                    End Date <SortIcon field="end_date" activeField={sortField} activeDir={sortDir} />
                  </th>
                  <th className={`${compactStyles.sortableHeader} ${compactStyles.numericCell}`} onClick={() => toggleSort('total_pnl')}>
                    Total PnL <SortIcon field="total_pnl" activeField={sortField} activeDir={sortDir} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((bt) => (
                  <tr
                    key={bt.backtest_name}
                    style={{
                      cursor: 'pointer',
                      ...(bt.backtest_name === selectedName ? { background: 'rgba(59,130,246,0.12)' } : {}),
                    }}
                    onClick={() => handleSelect(bt.backtest_name)}
                  >
                    <td style={{ fontWeight: 600, color: 'var(--tr-accent, #3b82f6)' }}>{bt.backtest_name}</td>
                    <td>{fmtDate(bt.start_date)}</td>
                    <td>{fmtDate(bt.end_date)}</td>
                    <td className={`${compactStyles.numericCell} ${bt.total_pnl >= 0 ? compactStyles.pnlPositive : compactStyles.pnlNegative}`}>
                      {fmtPnl(bt.total_pnl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Error boundary to catch missing context ───────────────────────── */

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class BacktestsErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      message: error.message || 'Unexpected error in Backtests widget.',
    };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error('BacktestsWidget render error:', error);
  }

  render() {
    if (this.state.hasError) {
      const msg = this.state.message;
      let displayMessage = msg || 'Unable to render Backtests widget.';

      if (msg.includes('AuthProvider')) {
        displayMessage =
          'AuthContext is not available. Ensure this widget is rendered inside an AuthProvider.';
      }

      return (
        <div style={{ padding: '1rem', color: 'var(--color-error, #e74c3c)' }}>
          <strong>Backtests unavailable</strong>
          <p style={{ marginTop: '0.5rem' }}>{displayMessage}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Self-contained Backtests widget.
 *
 * Loads saved backtests from DashboardService, renders a sortable table
 * with Name, Start Date, End Date, and Total PnL columns. Clicking a row
 * loads the backtest detail (statistics grid + chart) from DashboardService.
 * Detail panel shows a Stats/Config tab pair on the left and a
 * BacktestResultsChart on the right, with an Excel download button.
 */
export const BacktestsWidget: React.FC<WidgetInstanceProps> = (_props) => {
  return (
    <BacktestsErrorBoundary>
      <BacktestsInner />
    </BacktestsErrorBoundary>
  );
};

export default BacktestsWidget;
